import { memo, useCallback, type ComponentType } from 'react';
import { Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getToggleInputPanel,
  getToolbarType,
  handleAddIndicator,
  handleToggleInputPanel,
  toggleToolbarType,
} from 'redux/PlannerModeSlice';

import { BUILDER_MODE_V1_LAYOUT, BUILDER_MODE_V1_TOKENS } from '../constants';
import DataTabIcon from '../icons/assets/DataTabIcon';
import MapTabIcon from '../icons/assets/MapTabIcon';
import PropertyTabIcon from '../icons/assets/PropertyTabIcon';

import AddIndicatorButton from './AddIndicatorButton';
import BuilderModeToolbarTab from './BuilderModeToolbarTab';

type ViewTab = 'Property' | 'Data' | 'Map';

interface ViewTabConfig {
  id: ViewTab;
  label: string;
  iconSize: number;
  Icon: ComponentType<{ size?: number }>;
}

const VIEW_TABS: ViewTabConfig[] = [
  { id: 'Property', label: 'Property', iconSize: 18, Icon: PropertyTabIcon },
  { id: 'Data', label: 'Data', iconSize: 16, Icon: DataTabIcon },
  { id: 'Map', label: 'Map', iconSize: 18, Icon: MapTabIcon },
];

const BuilderModeSubToolbar = memo(() => {
  const dispatch = useDispatch();
  const toolbarType = useSelector(getToolbarType) as ViewTab;
  const toggleInputPanel = useSelector(getToggleInputPanel);

  const handleTabChange = useCallback(
    (tab: ViewTab) => {
      dispatch(handleAddIndicator(false));
      dispatch(toggleToolbarType(tab));
      if (toggleInputPanel && tab !== 'Property') {
        dispatch(handleToggleInputPanel());
      }
    },
    [dispatch, toggleInputPanel],
  );

  const handleAddNewIndicator = useCallback(() => {
    // Reset first so repeated clicks re-trigger the active table's add-row listener.
    dispatch(handleAddIndicator(false));
    requestAnimationFrame(() => {
      dispatch(handleAddIndicator(true));
    });
  }, [dispatch]);

  return (
    <Flex
      as='nav'
      aria-label='Builder view'
      w='100%'
      minH={BUILDER_MODE_V1_LAYOUT.subToolbarMinHeight}
      align='center'
      justify='space-between'
      gap={{ base: 3, md: 4 }}
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
      px={BUILDER_MODE_V1_LAYOUT.horizontalPadding}
      bg={BUILDER_MODE_V1_TOKENS.white}
      borderBottom='1px solid'
      borderColor={BUILDER_MODE_V1_TOKENS.border}
      flexShrink={0}
    >
      <Flex
        align='stretch'
        h={BUILDER_MODE_V1_LAYOUT.subToolbarMinHeight}
        minH={BUILDER_MODE_V1_LAYOUT.subToolbarMinHeight}
        borderRadius={BUILDER_MODE_V1_LAYOUT.subToolbarTabRadius}
        overflowX='auto'
        overflowY='hidden'
        flex='1'
        minW={0}
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {VIEW_TABS.map(({ id, label, iconSize, Icon }) => (
          <BuilderModeToolbarTab
            key={id}
            label={label}
            isActive={toolbarType === id}
            onClick={() => handleTabChange(id)}
            icon={<Icon size={iconSize} />}
          />
        ))}
      </Flex>

      <Flex
        justify='flex-end'
        flexShrink={0}
        w={{ base: '100%', md: 'auto' }}
        pl={{ base: 0, md: 2 }}
      >
        <AddIndicatorButton onClick={handleAddNewIndicator} />
      </Flex>
    </Flex>
  );
});

BuilderModeSubToolbar.displayName = 'BuilderModeSubToolbar';

export default BuilderModeSubToolbar;
